import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { LegacyStatus } from '@vendasta/notifications-sdk';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DAILY_DIGEST_TYPE, INSTANT_EMAIL_SECTION_TYPE } from '../constants';
import {
  AppSettingSection,
  LegacyNotificationPreferenceService,
  ProductNotifications,
} from './legacy-notification-preference.service';

@Component({
  selector: 'app-legacy-notification-preference',
  templateUrl: './legacy-notification-preference.component.html',
  styleUrls: ['./legacy-notification-preference.component.scss'],
  standalone: false,
})
export class LegacyNotificationPreferenceComponent implements OnInit, OnDestroy {
  @Input()
  partnerId: string;
  @Input()
  accountGroupId: string;
  @Input()
  marketId: string;
  @Input()
  userId: string;
  @Input()
  bulk: boolean;
  @Output()
  changes = new EventEmitter<AppSettingSection>();

  selectedNotification: ProductNotifications;
  notificationSections: ProductNotifications[];

  errorGettingSettings = false;
  errorMessage = 'USER_NOTIFICATIONS.ERROR_GETTING_NOTIFICATION_SETTINGS';
  disabledSections: string[] = [];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(private accountGroupNotificationsService: LegacyNotificationPreferenceService) {}

  ngOnInit(): void {
    this.accountGroupNotificationsService
      .getSettings(this.partnerId, this.marketId, this.userId, this.accountGroupId, this.bulk)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (response) => {
          this.errorGettingSettings = false;
          this.selectedNotification = response[0];
          this.notificationSections = response;
          this.checkForDisabledSections();
        },
        (err) => {
          console.error(err);
          this.errorGettingSettings = true;
          if (err.statusCode && err.statusCode == 403) {
            if (this.bulk) {
              this.errorMessage = 'USER_NOTIFICATIONS.NO_ACCESS_TO_MARKET_ADMIN_BULK';
            } else {
              this.errorMessage = 'USER_NOTIFICATIONS.NO_ACCESS_TO_MARKET_ADMIN_DEFAULT_PARTNER';
            }
          }
        },
      );
  }

  selectNotification(product: ProductNotifications): void {
    this.selectedNotification = product;
    this.checkForDisabledSections();
  }

  selectionUpdated(notificationSettings: AppSettingSection): void {
    const sectionIndex = this.notificationSections.findIndex(
      (notificationSection) => notificationSection.productId === notificationSettings.productId,
    );
    if (sectionIndex !== -1) {
      const notificationSettingIndex = this.notificationSections[sectionIndex].sections.findIndex(
        (section) => section.sectionId === notificationSettings.sectionId,
      );

      if (notificationSettingIndex !== -1) {
        this.notificationSections[sectionIndex].sections[notificationSettingIndex] = notificationSettings;
      }
    }

    this.changes.emit(notificationSettings);
  }

  private checkForDisabledSections(): void {
    this.disabledSections = [];
    if (!this.bulk) {
      return;
    }
    // Check if the instant email is disabled
    if (
      this.notificationSections &&
      this.notificationSections.length > 0 &&
      this.notificationSections[0].sections.length > 1
    ) {
      if (this.notificationSections[0].sections[0].value === LegacyStatus.STATUS_DISABLED) {
        this.disabledSections.push(INSTANT_EMAIL_SECTION_TYPE);
      }
      // Check if the daily digest is disabled
      if (this.notificationSections[0].sections[1].value === LegacyStatus.STATUS_DISABLED) {
        this.disabledSections.push(DAILY_DIGEST_TYPE);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
